import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, duffelLogo } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { FlightCard } from "@/components/FlightCard";
import { AirportAutocomplete } from "@/components/AirportAutocomplete";
import { Button } from "@/components/ui/button";
import { Loader2, SlidersHorizontal, Search, ArrowRight, PlaneTakeoff } from "lucide-react";

export default function Flights() {
  const [params, setParams] = useSearchParams();
  const [flights, setFlights] = useState([]);
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("price");
  const [stopFilter, setStopFilter] = useState("all");

  const [origin, setOrigin] = useState(null);
  const [dest, setDest] = useState(null);
  const [cabin, setCabin] = useState(params.get("cabin") || "Economy");
  const [departDate, setDepartDate] = useState(params.get("depart_date") || "");
  const [returnDate, setReturnDate] = useState(params.get("return_date") || "");
  const pax = {
    adults: Number(params.get("adults")) || 1,
    children: Number(params.get("children")) || 0,
    infants: Number(params.get("infants")) || 0,
  };

  const load = useCallback(async () => {
    const o = params.get("origin"), d = params.get("destination");
    const c = params.get("cabin") || "Economy";
    const dd = params.get("depart_date"), rd = params.get("return_date");
    setLoading(true);
    try {
      if (o && d) {
        const { data } = await api.get("/flights/search", { params: { origin: o, destination: d, cabin: c, ...(dd ? { depart_date: dd } : {}), ...(rd ? { return_date: rd } : {}) } });
        setFlights(data.flights);
        setRoute(data.origin && data.destination ? { o: data.origin, d: data.destination } : null);
      } else {
        const { data } = await api.get("/flights");
        setFlights(data);
        setRoute(null);
      }
    } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const runSearch = () => {
    const p = new URLSearchParams();
    if (origin?.code) p.set("origin", origin.code);
    if (dest?.code) p.set("destination", dest.code);
    p.set("cabin", cabin);
    if (departDate) p.set("depart_date", departDate);
    if (returnDate) p.set("return_date", returnDate);
    if (pax.adults) p.set("adults", pax.adults);
    if (pax.children) p.set("children", pax.children);
    if (pax.infants) p.set("infants", pax.infants);
    setParams(p);
  };

  let shown = stopFilter === "all" ? flights : flights.filter((f) => stopFilter === "direct" ? f.stops === "Direct" : f.stops !== "Direct");
  shown = [...shown].sort((a, b) => sort === "price" ? a.price - b.price : sort === "duration" ? a.duration.localeCompare(b.duration) : a.airline.localeCompare(b.airline));

  return (
    <div>
      <PageHero title="Search flights worldwide" subtitle="Compare fares to any airport on earth — 5,400+ destinations." image="https://images.pexels.com/photos/1493756/pexels-photo-1493756.jpeg" />

      {/* Search bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-8 relative z-20">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-4 flex flex-col md:flex-row gap-3 items-stretch">
          <div className="flex-1 bg-white rounded-xl border border-slate-200 px-4 py-2.5 focus-within:ring-2 focus-within:ring-[#328CC1]">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">From</div>
            <AirportAutocomplete testid="flights-origin" placeholder="City or airport" value={origin ? `${origin.city} (${origin.code})` : (params.get("origin") || "")} onSelect={setOrigin} />
          </div>
          <div className="flex-1 bg-white rounded-xl border border-slate-200 px-4 py-2.5 focus-within:ring-2 focus-within:ring-[#328CC1]">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">To</div>
            <AirportAutocomplete testid="flights-destination" placeholder="City or airport" value={dest ? `${dest.city} (${dest.code})` : (params.get("destination") || "")} onSelect={setDest} />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-2.5 focus-within:ring-2 focus-within:ring-[#328CC1] md:w-40">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Depart</div>
            <input data-testid="flights-depart-date" type="date" value={departDate} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setDepartDate(e.target.value)} className="w-full outline-none text-sm font-medium bg-transparent" />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-2.5 focus-within:ring-2 focus-within:ring-[#328CC1] md:w-40">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Return</div>
            <input data-testid="flights-return-date" type="date" value={returnDate} min={departDate || new Date().toISOString().slice(0, 10)} onChange={(e) => setReturnDate(e.target.value)} className="w-full outline-none text-sm font-medium bg-transparent" />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-2.5 focus-within:ring-2 focus-within:ring-[#328CC1] md:w-40">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Cabin</div>
            <select data-testid="flights-cabin" value={cabin} onChange={(e) => setCabin(e.target.value)} className="w-full outline-none text-sm font-medium bg-transparent">
              <option>Economy</option><option>Premium Economy</option><option>Business</option><option>First</option>
            </select>
          </div>
          <Button data-testid="flights-search-btn" onClick={runSearch} className="rounded-xl bg-[#FF6B35] hover:bg-[#e85a26] text-white font-semibold px-8 py-4 h-auto">
            <Search className="w-5 h-5 mr-2" /> Search
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {route && (
          <div className="flex items-center gap-3 mb-6 font-display text-xl font-bold text-slate-900">
            <span>{route.o.city} <span className="text-slate-400">({route.o.code})</span></span>
            <ArrowRight className="w-5 h-5 text-[#FF6B35]" />
            <span>{route.d.city} <span className="text-slate-400">({route.d.code})</span></span>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <p className="text-slate-600 text-sm" data-testid="flights-count">{loading ? "Searching…" : `${shown.length} flights ${route ? "found" : "on offer"}`}</p>
          <div className="flex items-center gap-3 text-sm">
            <select data-testid="flights-stops" value={stopFilter} onChange={(e) => setStopFilter(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-1.5 bg-white outline-none">
              <option value="all">All flights</option><option value="direct">Direct only</option><option value="stops">1+ Stops</option>
            </select>
            <div className="flex items-center gap-2"><SlidersHorizontal className="w-4 h-4 text-slate-400" />
              <select data-testid="flights-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-1.5 bg-white outline-none">
                <option value="price">Cheapest</option><option value="duration">Fastest</option><option value="airline">Airline A-Z</option>
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center py-20" data-testid="flights-loading">
            <div className="flex items-center gap-4 mb-5">
              {["BA", "EK", "QR", "TK", "LH", "AI"].map((c, i) => (
                <img key={c} src={duffelLogo(c)} alt="" className="h-6 w-auto object-contain opacity-0 animate-fade-up"
                  style={{ animationDelay: `${i * 0.12}s`, animationFillMode: "forwards" }}
                  onError={(e) => { e.target.style.display = "none"; }} />
              ))}
            </div>
            <div className="flex items-center gap-2 text-[#0B3C5D] font-display font-bold text-lg">
              <Loader2 className="w-5 h-5 animate-spin text-[#FF6B35]" /> Searching live fares…
            </div>
            <p className="text-xs text-slate-400 mt-2 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00b67a] animate-pulse" /> Checking real-time prices across airlines
            </p>
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">{shown.map((f) => <FlightCard key={f.id} f={f} pax={pax} />)}</div>
        )}
        {!loading && shown.length === 0 && (
          <div className="text-center py-20 text-slate-500">
            <PlaneTakeoff className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p>No flights for this route. Pick an origin and destination above to search worldwide.</p>
          </div>
        )}
      </div>
    </div>
  );
}
